/**
 * 「新しいタブを先に開いてから URL を入れる」を安全に行うヘルパ。
 *
 * ## なぜヘルパにするのか（2026-09-05 に報告された）
 *
 * ポップアップブロックは「クリックと同期的に開かれた `window.open`」しか
 * 通さない。だから URL を取りに行く前に空のタブを開き、返ってきてから
 * `location` を入れる、という形が要る。この形自体は正しい。
 *
 * 問題は `window.open('', '_blank', 'noopener')` と書いていたこと。
 * **`noopener` を指定すると `window.open()` は仕様上 `null` を返す**
 * （開いた側から参照を持てなくするのが `noopener` の意味そのもの）。
 * その結果:
 *
 *   - 空のタブは開くが、誰も URL を入れないので**白いまま残る**
 *   - `win` が null なので後始末のコードが `window.location` へ落ちて
 *     **元の画面まで遷移してしまう**
 *
 * 「新しいタブで開く」つもりの操作が「白いタブが増えたうえ、元の画面も
 * 移動する」になっていた。4 画面（請求書・エクスポート・メールアドレス詳細・
 * Webメール）が同じ書き方をしていた。
 *
 * ## 代わりに何をするか
 *
 * `noopener` は渡さず、開いた直後に `win.opener = null` を代入する。
 * 参照を切るという目的は同じで、**こちらは戻り値が返る**。
 */

export interface PendingTab {
  /** 取得した URL をそのタブへ入れる。 */
  navigate(url: string): void;
  /** 取得に失敗したときに閉じる。白いタブを残さない。 */
  close(): void;
}

/**
 * クリックと同期的に空のタブを開く。
 *
 * ブロックされた場合は `null` になる。そのときは**現在のタブで開く**
 * （何も起きないより、遷移してでも目的を果たすほうがよい）。
 */
export function openPendingTab(): PendingTab {
  // **`noopener` を features に渡さない。** 渡すと null が返る（上の説明）。
  const win = window.open('', '_blank');
  if (win) {
    // `noopener` と同じ効果を手で作る。開いた先から `window.opener` 経由で
    // こちらを触られないようにする。
    try {
      win.opener = null;
    } catch {
      // 一部のブラウザで代入できないことがある。参照が切れないだけで
      // 遷移そのものは成立するので、ここで失敗しても続行する。
    }
  }
  return {
    navigate(url: string) {
      if (win && !win.closed) win.location.href = url;
      else window.location.href = url;
    },
    close() {
      if (win && !win.closed) win.close();
    },
  };
}
